import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import { STADIUM_SCHEMA, STADIUM_CLAIM_SCHEMA, MAPPED_STADIUM_SCHEMA } from '../schema/stadiumSchema';
import { ClaimFilters } from '../components/claim/claimFilters';
import { Progress } from '../components/util/progress';
import {
  GOLDEN_STADIUM_AND_CLAIMS,
  SAVE_STADIUM_CLAIM,
  OVERRIDE_STADIUM_CLAIM,
  INSERT_GOLDEN_STADIUM,
} from '../hasura/queries/stadium/stadiumClaims';
import { Error } from '../components/util/notifications';
import ClaimHistoryTable from '../components/table/claimHistoryTable';
import { DELETE_STADIUM, SIMILAR_STADIUMS } from '../hasura/queries/stadium/stadiums';
import { blankSchema } from '../util/schema';
import MappingView from '../components/mapping/mappingView';
import { DELETE_STADIUM_MAPPING, VIEW_STADIUM_MAPPINGS } from '../hasura/queries/stadium/stadiumMapping';
import { SimilarEntity } from '../components/mapping/SimilarEntity';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenStadiumView = ({ stadiumId, isNewStadium, history }) => {
  const historyPath = history.location.search;

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewStadium ? blankSchema(STADIUM_SCHEMA) : {});
  const [overrides, setOverrides] = useState({});
  const [claimMode, setClaimMode] = useState(isNewStadium ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);

  const setNewEntityMode = () => {
    setClaimMode(CLAIM_MODE_NEW_ENTITY);
    setClaimStatuses(blankSchema(STADIUM_SCHEMA));
  };

  const setViewMode = () => {
    setClaimMode(CLAIM_MODE_VIEW);
    setClaimStatuses({});
  };

  useEffect(() => (historyPath === '?new=new' ? setNewEntityMode() : setViewMode()), [historyPath]);

  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(GOLDEN_STADIUM_AND_CLAIMS, {
    variables: {
      stadium_id: stadiumId,
    },
  });
  if (loading) return <Progress />;
  let allClaims = [];
  data &&
    data.statsbomb_stadium &&
    data.statsbomb_stadium[0] &&
    data.statsbomb_stadium[0].mapping_stadiums.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_stadiums];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }

  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {};

    const claim = allClaims.filter(c => {
      return c.claim_stadium_uuid === claimUUID;
    })[0];
    if (claim) {
      STADIUM_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    STADIUM_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(blankSchema(STADIUM_SCHEMA));
  };

  const getGoldenStadiumId = insertedStadium =>
    insertedStadium?.data?.insert_statsbomb_stadium?.returning?.[0]?.stadium_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_stadium_uuid);
  };

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <h2>Stadium Singular: {isNewStadium ? ' New Stadium' : stadiumId}</h2>

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="stadium"
              schema={STADIUM_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{ headerValue: 'Stadium Name', bodyValue: data.statsbomb_stadium[0]?.stadium_name }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}
        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <MappingView
            type="stadium"
            recordId={stadiumId}
            schema={MAPPED_STADIUM_SCHEMA}
            subscription={VIEW_STADIUM_MAPPINGS}
            deleteMutation={DELETE_STADIUM_MAPPING}
            deleteEntityMutation={DELETE_STADIUM}
            entityKey="provider_stadium_id"
            styles={{ tableContatinerClasses, buttonClasses }}
          />
        )}
        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Stadium Attributes {selectedClaimId && ' And Claim Values'}</h3>

          <SingleClaimTable
            goldenEntity={data.statsbomb_stadium[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_stadium_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={STADIUM_SCHEMA}
            saveMutation={SAVE_STADIUM_CLAIM}
            overrideMutation={OVERRIDE_STADIUM_CLAIM}
            createMutation={INSERT_GOLDEN_STADIUM}
            sourcePK="provider_stadium_id"
            claimPK="claim_stadium_uuid"
            goldenEntityPK="stadium_id"
            getGoldenEntityId={getGoldenStadiumId}
            entityType="stadium"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            similarEntityTable={SimilarEntity}
            similarEntityQuery={SIMILAR_STADIUMS}
            similarEntityQueryObject="statsbomb_similar_stadiums"
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

const Stadium = ({ history }) => {
  const query = new URLSearchParams(useLocation().search);
  const isNewStadium = query.get('new');
  const stadiumId = isNewStadium ? '0' : query.get('stadium_id');

  return <GoldenStadiumView isNewStadium={!!isNewStadium} {...{ stadiumId, history }} />;
};

export default Stadium;
